import { Button, Modal, ModalProps } from 'antd'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { FunctionComponent, ReactNode, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Beta } from '@/components/common/beta-tag'
import { LevelType } from '@/components/common/k-score/get-level'
import routes from '@/hooks/use-authorization/routes'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import useChartVisible from '@/hooks/use-chart-visible'
import { useI18n } from '@/hooks/use-i18n'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })

const LABELS_I18N_KEYS: I18nId[] = [
  'kol:follow_level',
  'kol:fan_growth',
  'kol:fan_engagement',
  'kol:video_popularity',
  'kol:publish_frequency',
]

interface RadarChartModalProps extends Pick<ModalProps, 'open'> {
  data: number[]
  onClose: VoidFunction
  type: LevelType
  levelI18nId: I18nId
  descriptionI18nId: I18nId
  score: number
  isKolInfoLocked: boolean
}

const RadarChartModal: FunctionComponent<RadarChartModalProps> = ({
  open,
  onClose,
  data,
  type,
  levelI18nId,
  score,
  isKolInfoLocked,
  descriptionI18nId,
}) => {
  const { chartRef: radarChartRef, isVisible } = useChartVisible()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { locale } = useI18n()
  const breakpoint = useBreakpoint()

  const options = {
    yaxis: {
      show: false,
      min: 0,
      max: 100,
      tickAmount: 5,
    },
    tooltip: {
      enabled: false,
    },
    markers: {
      colors: theme.colors.brand.primary,
      size: 4,
    },
    stroke: { colors: [theme.colors.brand.primary], width: 2 },
    fill: {
      opacity: 0.2,
      colors: [theme.colors.kolrBrand.primary],
    },
    chart: {
      toolbar: {
        show: false,
      },
    },
  }

  const labels = useMemo(() => {
    return LABELS_I18N_KEYS.map((key) => formatMessage({ id: key }))
  }, [formatMessage])

  const series = isVisible ? [{ data }] : []

  return (
    <Modal
      footer={null}
      open={open}
      onCancel={(): void => {
        onClose()
      }}
    >
      <Wrapper ref={radarChartRef}>
        <Header>
          <Title>
            K-Score :{' '}
            {isKolInfoLocked
              ? '??'
              : `${score} ${formatMessage({ id: levelI18nId })}`}
          </Title>
        </Header>

        {!isKolInfoLocked && type !== LevelType.Bad && (
          <>
            <Description style={{ paddingBottom: 20 }}>
              {formatMessage({ id: descriptionI18nId })}
            </Description>
            <ChartWrapper>
              <Chart
                key={locale}
                options={{ ...options, labels }}
                series={series}
                type='radar'
                width={breakpoint === 'PC' ? 500 : 250}
              />
            </ChartWrapper>
          </>
        )}
        {((): ReactNode => {
          if (isKolInfoLocked) {
            return (
              <Link href={routes.workspacePlan.stringifyUrl({})}>
                <LinkButton type='link'>
                  {formatMessage({ id: 'kol:score_cta_description' })}
                </LinkButton>
              </Link>
            )
          }

          if (type === LevelType.Bad) {
            return (
              <Description>
                {formatMessage({ id: descriptionI18nId })}
              </Description>
            )
          }

          return (
            <Description alignSelf='center'>
              {formatMessage({ id: 'kol:metric_distribution' })}
            </Description>
          )
        })()}
      </Wrapper>
    </Modal>
  )
}

const LinkButton = styled(Button)`
  color: ${({ theme }): string => theme.colors.gray8};
  align-self: flex-start;
  span {
    text-decoration: underline !important;
  }
`

const Title = styled.div`
  display: flex;
  align-self: flex-start;
  color: ${({ theme }): string => theme.colors.gray10};
  font-weight: 700;
  font-size: 24px;
`

const Header = styled.div`
  display: flex;
  align-self: flex-start;
  align-items: center;
  ${Beta} {
    height: fit-content;
    margin-left: 10px;
  }
`

const ChartWrapper = styled.div`
  margin: 0 0 -50px 0;
`

const Description = styled.div<{ alignSelf?: 'flex-start' | 'center' }>`
  color: ${({ theme }): string => theme.colors.gray10};
  align-self: ${({ alignSelf }): string => alignSelf ?? 'flex-start'};
  padding: 24px 0 0;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export default RadarChartModal
